import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginPrincipalService, Pagination,PermissionTemplateService, PermissionWizardService } from 'blg-akaun-ts-lib';
import * as moment from 'moment';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { Observable, } from 'rxjs';
import { SubSink } from 'subsink2';
import { PermWizardSelectors } from '../../../permission-wizard-controller/selectors';
import { PermWizardActions } from '../../../permission-wizard-controller/actions';
import { PermissionWizardModel } from '../../../../../models/permission-wizard.model';
import { PermWizardListingSearchModel } from '../../../../../models/advanced-search-models/perm-wizard-listing-search.model';
import { SessionStates } from '../../../../../modules/session/session-controller/states';
import { SessionSelectors } from '../../../../../modules/session/session-controller/selectors';
import { SearchQueryModel } from '../../../../../models/query.model';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-permission-wizard-listing',
  templateUrl: './permission-wizard-listing.component.html',
  styleUrls: ['./permission-wizard-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermissionWizardListingComponent extends ViewColumnComponent {


  protected readonly index = 0;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  toggleColumn$: Observable<boolean>;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  searchModel = PermWizardListingSearchModel;

  // initialise ag grid
  gridApi;
  gridOptions = {
    serverSideDatasource: (params) => {
      // call the API to get the data for the current page
      this.loadData().subscribe((response) => {
        const rowCount = params.request.endRow - params.request.startRow;
        const rowsThisPage = response.items.slice(0, 500);
        params.successCallback(rowsThisPage, response.totalItems);
      });
    },
  };
  columnsDefs;
  rowData;
  rowSelection = 'single';

  // this applet guid
  appletGuid = sessionStorage.getItem('appletGuid');
  tenantGuid;
  loadData: any;


  // @ViewChild(PaginationComponent, { static: false }) private paginationComponent: PaginationComponent;

  //translation variable
  title: string;
  code_text: string;
  name_text: string;
  description_text: string;
  no_of_permissions_text: string;
  created_by_text: string;
  created_date_text: string;
  modified_date_text: string;

  constructor(
    private permWizardService: PermissionWizardService,
    private permTemplateService: PermissionTemplateService,
    private viewColFacade: PermissionFacade,
    private store: Store,
    private componentStore: ComponentStore<{ localState: LocalState }>,
    private appLoginPrincipalService: AppLoginPrincipalService,
    private readonly sessionStore: Store<SessionStates>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.permission_template_listing;
        this.code_text = res.code;
        this.name_text = res.name;
        this.description_text = res.description;
        this.no_of_permissions_text = res.no_of_permissions;
        this.created_by_text = res.created_by;
        this.created_date_text = res.created_date;
        this.modified_date_text = res.modified_date;
      });
    } else {
      // Fallback if translation service is unavailable
      this.title = 'Permission Template Listing';
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.description_text = 'Description';
      this.no_of_permissions_text = 'No. of Permissions';
      this.created_by_text = 'Created By';
      this.created_date_text = 'Created Date';
      this.modified_date_text = 'Modified Date';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({ localState: a });
    });
    this.sessionStore.select(SessionSelectors.selectAppletLoginSubjectLink).subscribe(data => {
      this.tenantGuid = data.tenant_guid.toString();
    })

    // define column definitions
    this.columnsDefs = [
      {
        headerName: this.code_text,
        field: 'app_perm_template_hdr.code',
        minWidth: 300,
        cellStyle: { textAlign: 'left' },
        // floatingFilter: true
      },
      {
        headerName: this.name_text,
        field: 'app_perm_template_hdr.name',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.description_text,
        field: 'app_perm_template_hdr.description',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.no_of_permissions_text,
        field: 'app_perm_template_lines.length',
        minWidth: 100,
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.created_by_text,
        field: 'app_login_principal.principal_id',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.created_date_text,
        field: 'app_perm_template_hdr.created_date',
        headerClass: 'text-center',
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      },
      {
        headerName: this.modified_date_text,
        field: 'app_perm_template_hdr.updated_date',
        headerClass: 'text-center',
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      }
    ]


  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
  onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: true,
        deactivateList: false
      });
    // trigger the add permission set component to be shown
    this.viewColFacade.onNextAndReset(this.index, 1);
  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    // const datasource = this.getDatasource();
    // this.gridApi.setServerSideDatasource(datasource);
    this.getDatasource();

    this.store.select(PermWizardSelectors.selectUpdatePermWizardListing)
    .subscribe(x => {
      if (x) {
        if (this.gridApi.getDisplayedRowCount() > 0) {
          this.gridApi.refreshServerSideStore();
        } else {
          this.gridApi.refreshServerSideStore(
            {purge: true}
          )
        }
        this.store.dispatch(PermWizardActions.setUpdatePermWizardListing({update: false}));
      }
    })
  }
  onRowData() {
    this.gridApi.setRowData(this.rowData);
  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
   getDatasource(conditionalCriteria?) {
    let offset = 0;
    let limit = 500;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    ];
    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    const sortCriteria = [
      { columnName: 'orderBy', value: 'updated_date' },
      { columnName: 'order', value: 'DESC' }
    ];

    this.subSink.sink = this.permTemplateService.getByLogin(this.appletGuid.toString(), this.tenantGuid.toString(), this.apiVisa)
    .subscribe(a => {
      // put in data to the grid
      console.log("DATA::::1",a.data)
      // let data = sortOn(a.data).filter(entity => agFilter.by(entity));
      // console.log("DATA::::2",data )
      // data = data.filter(perm => perm.app_perm_template_hdr.applet_guid == this.appletGuid)
      // const totalRecords = agFilter.isFiltering ? a.totalRecords :  data.length;
      // force pagination to go to first page if filter is applied
      // console.log("DATA::::3",data )
      // if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) {
      //   this.paginationComponent.firstPage();
      // }
      this.rowData = a.data;
      this.gridApi.setRowData(this.rowData);
    }
    )

    // return {
    //   getRows: grid => {
    //     const sortModel = grid.request.sortModel;
    //     const filterModel = grid.request.filterModel;
    //     const sortOn = pageSorting(sortModel);
    //     const agFilter = pageFiltering(filterModel);

    //     // only change offset and limit if no filterModel or sortModel is present
    //     // if (Object.keys(filterModel).length === 0 && sortModel.length === 0) {
    //     //   offset = grid.request.startRow;
    //     //   limit = grid.request.endRow - offset;
    //     // }

    //     offset = grid.request.startRow;
    //     limit = grid.request.endRow - offset;

    //     const pagination = new Pagination(offset, limit, criteria, sortCriteria);
    //     // const pagination = new Pagination(offset, limit, [
    //     //   {columnName: 'calcTotalRecords', operator: '=', value: 'true'}
    //     //   // { columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    //     // ])

    //     this.subSink.sink = this.permTemplateService.getByLogin(this.appletGuid.toString(), this.tenantGuid.toString(), this.apiVisa)
    //       .subscribe(a => {
    //         // put in data to the grid
    //         console.log("DATA::::1",a.data)
    //         let data = sortOn(a.data).filter(entity => agFilter.by(entity));
    //         console.log("DATA::::2",data )
    //         // data = data.filter(perm => perm.app_perm_template_hdr.applet_guid == this.appletGuid)
    //         // const totalRecords = agFilter.isFiltering ? a.totalRecords :  data.length;
    //         // force pagination to go to first page if filter is applied
    //         // console.log("DATA::::3",data )
    //         if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) {
    //           this.paginationComponent.firstPage();
    //         }
    //         grid.success({
    //           rowData: data,
    //           // rowCount: a.totalRecords
    //         });
    //       }, err => {
    //         grid.fail();
    //       });
    //   }
    // };
  }


  SQLGuids: string[] = null;

  // pagination = new Pagination();

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
  getUser(subjectGuid: string) {

    // // initialise new pagination
    // const pagination = new Pagination();
    // // add necessary criteria
    // pagination.conditionalCriteria.push({
    //   columnName: 'subject_guid',
    //   operator: '=',
    //   value: subjectGuid
    // });
    // pagination.conditionalCriteria.push({
    //   columnName: 'principal_type',
    //   operator: '=',
    //   value: 'EMAIL_USERNAME'
    // });

    // return this.appLoginPrincipalService.getByCriteria(pagination, this.apiVisa)
    //   .pipe(
    //     map((subject) => subject.data[0])
    //   )

  }

  /**
   * This function is called every time the user clicks on a row in the
   * AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
  onRowClicked(permWizard: PermissionWizardModel) {
    this.store.dispatch(PermWizardActions.selectPermWizard({ permWizard }));
    console.log('Selected Perm Hdr',PermWizardActions.selectPermWizard({ permWizard }))
    this.goToEditPerm();
  }

  goToEditPerm() {
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: false,
        deactivateList: true
      });
    // trigger the next view column
    this.viewColFacade.onNextAndReset(this.index, 1);
  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
  onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  /**
   * This function calls the subquery service to get the relevant guids
   * that match the details of the search query and sets the datasource
   * of the AG Grid accordingly.
   *
   * @param query : the search query
   * @param table : the relevant table to search for the search query
   */
  // searchQuery(query: string, table: string) {
  //   // using the subquery service to get the relevant guids
  //   const query$ = this.subqueryService
  //     .post({ 'subquery': query, 'table': table }, this.apiVisa)
  //     .pipe(
  //       switchMap(resp => of(resp))
  //     );

  //   // if there is response, find the responding vendor guids and set datasource
  //   this.subSink.sink = query$.pipe(
  //     filter(a => a.data.length > 0)
  //   ).subscribe(resp => {
  //     const criteria = [
  //       // combine all the guids together separated by commas
  //       { columnName: 'guids', operator: '=', value: resp.data.join(',') }
  //     ];

  //    // const datasource = this.getDatasource(criteria);
  //    // this.gridApi.setServerSideDatasource(datasource);
  //   });

  //   // if there is no response, clear the table as this means nothing
  //   // in the database matches
  //   this.subSink.sink = query$.pipe(
  //     filter(a => a.data.length === 0)
  //   ).subscribe(_ => serverSideClear(this.gridApi));
  // }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */

  getSearchQuery(criteriaQuery){
    let offset;
    let limit;
    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    ];

    if (criteriaQuery.length >= 1) {
      criteria = criteria.concat(criteriaQuery);
      const pagination = new Pagination(offset, limit, criteria);
      this.subSink.sink = this.permWizardService.getByCriteria(pagination,this.apiVisa)
        .subscribe(a => {
          this.rowData = a.data;
          this.gridApi.setRowData(this.rowData);
        })
    } else {
      this.getDatasource();
    }
  }

  onSearch(e: SearchQueryModel) {
    let advanceSearchCode: string;
    let advanceSearchName: string;
    let advanceSearchDescription: string;
    let criteria = [];

    if(e.keyword !== null && e.keyword !== "" && e.keyword !== undefined){
      advanceSearchCode = e.keyword.toUpperCase();
      criteria.push({ columnName: 'advance_search_code', operator: '=', value: advanceSearchCode });
      this.getSearchQuery(criteria);
    } else {
      criteria = [];

      advanceSearchCode = this.searchModel.form.controls["advanceSearchCode"].value as string;
      advanceSearchName = this.searchModel.form.controls["advanceSearchName"].value as string;
      advanceSearchDescription = this.searchModel.form.controls["advanceSearchDescription"].value as string;

      if (advanceSearchCode !== null && advanceSearchCode !== "") {
        criteria.push({ columnName: 'advance_search_code', operator: '=', value: advanceSearchCode.toUpperCase() });
      }
      if (advanceSearchName !== null && advanceSearchName !== "") {
        criteria.push({ columnName: 'advance_search_name', operator: '=', value: advanceSearchName });
      }
      if (advanceSearchDescription !== null && advanceSearchDescription !== "") {
        criteria.push({ columnName: 'advance_search_description', operator: '=', value: advanceSearchDescription });
      }

      this.getSearchQuery(criteria);
    }
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
